import {
  CatalogEntity,
  EstateSearchProps,
  MarketingType,
  ObjectType,
} from '@portals/sip-client-data/src/general/ApiClientTypes';
import { ConfigProvider } from '@portals/sip-client-data/src/general/Config';
import { getFixedT } from 'i18next';
import { find } from 'lodash-es';

import { Criteria, FilterConfigFormat } from '../../../config';
import { formatterFunction } from '../../../hooks/usePriceFormatter/usePriceFormatter';
import { getMarketingTypeEstateTypeIdLabel, getMarketingTypeObjectTypeLabel } from '../../../utils';
import { getFilterConfig as getFilterConfigEstateTypeId } from '../../../utils/estateTypeIdUtils';
import { getFilterConfig as getFilterConfigObjectType } from '../../../utils/objectTypeUtils';
const t = getFixedT(null, 'core-immobilien');

export type FilterConfigItemEstateTypeId = {
  marketingType: MarketingType;
  estateTypeId: number;
  fields: Criteria[];
};

export type FilterConfigItemObjectType = {
  marketingType: MarketingType;
  objectType: ObjectType;
  fields: Criteria[];
};

const filterConfigEstateTypeId = getFilterConfigEstateTypeId(FilterConfigFormat.Criteria);
const filterConfigObjectType = getFilterConfigObjectType(FilterConfigFormat.Criteria);

type FindFilterConfigProps = {
  marketingType: MarketingType;
  estateTypeId?: number;
  objectType?: ObjectType;
};

export const findFilterConfig = ({
  marketingType,
  estateTypeId,
  objectType,
}: FindFilterConfigProps): FilterConfigItemEstateTypeId | FilterConfigItemObjectType | undefined => {
  // TODO: REMOVE AFTER IMMOBILIEN API INTEGRATION: switch
  const useImmobilienApi = ConfigProvider.getConfig().get('USE_IMMOBILIEN_API') === 'true';
  if (useImmobilienApi) {
    return find(filterConfigEstateTypeId, { marketingType, estateTypeId });
  }
  return find(filterConfigObjectType, { marketingType, objectType });
};

export function nullable<T>(value: T | null | undefined): T | null {
  return value == null || String(value).trim() === '' ? null : value;
}

export const getFormattedLabelValuePair = (
  field: Criteria,
  filterValues: EstateSearchProps,
  priceFormatter: formatterFunction,
  squareMetersFormatter: formatterFunction,
  roomFormatter: formatterFunction
): { label: string; value: string } => {
  switch (field) {
    case Criteria.LIVING_SPACE: {
      const { minLivingSpace, maxLivingSpace } = filterValues;
      return {
        label: t('savedSearches.livingSpace'),
        value: squareMetersFormatter(nullable(minLivingSpace), nullable(maxLivingSpace), t('savedSearches.undefined')),
      };
    }
    case Criteria.PRICE: {
      const { minPrice, maxPrice } = filterValues;
      return {
        label: t('price'),
        value: priceFormatter(nullable(minPrice), nullable(maxPrice), t('savedSearches.undefined')),
      };
    }
    case Criteria.ROOMS: {
      const { minNumberRooms, maxNumberRooms } = filterValues;
      return {
        label: t('savedSearches.rooms'),
        value: roomFormatter(nullable(minNumberRooms), nullable(maxNumberRooms), t('savedSearches.undefined')),
      };
    }
    case Criteria.RENT: {
      const { minRent, maxRent } = filterValues;
      return {
        label: t('price'),
        value: priceFormatter(nullable(minRent), nullable(maxRent), t('savedSearches.undefined')),
      };
    }
    case Criteria.PROPERTY_SIZE: {
      const { minPropertySize, maxPropertySize } = filterValues;
      return {
        label: t('savedSearches.propertySize'),
        value: squareMetersFormatter(
          nullable(minPropertySize),
          nullable(maxPropertySize),
          t('savedSearches.undefined')
        ),
      };
    }
    case Criteria.MARKET_VALUE: {
      const { minMarketValue, maxMarketValue } = filterValues;
      return {
        label: t('price'),
        value: priceFormatter(nullable(minMarketValue), nullable(maxMarketValue), t('savedSearches.undefined')),
      };
    }
    case Criteria.LEASE: {
      const { minLease, maxLease } = filterValues;
      return {
        label: t('price'),
        value: priceFormatter(nullable(minLease), nullable(maxLease), t('savedSearches.undefined')),
      };
    }
    case Criteria.TOTAL_SPACE: {
      const { minTotalSpace, maxTotalSpace } = filterValues;
      return {
        label: t('savedSearches.totalSpace'),
        value: squareMetersFormatter(nullable(minTotalSpace), nullable(maxTotalSpace), t('savedSearches.undefined')),
      };
    }
    default:
      return { label: '', value: '' };
  }
};

export const getFormattedLabel = (
  searchParams: EstateSearchProps,
  catalogsEstateSubTypes: CatalogEntity[],
  labelType: 'location' | 'object'
): string => {
  // TODO: REMOVE AFTER IMMOBILIEN API INTEGRATION: switch
  const useImmobilienApi = ConfigProvider.getConfig().get('USE_IMMOBILIEN_API') === 'true';

  const { zipCityEstateId, perimeter, estateTypeId, objectType, marketingType } = searchParams;
  const city = useImmobilienApi ? zipCityEstateId?.split('__')[1] : zipCityEstateId;

  switch (labelType) {
    case 'location':
      return city && perimeter
        ? `${city} ${t('savedSearches.perimeter', { value: perimeter })}`
        : city
        ? city
        : t('savedSearches.undefined');

    case 'object':
      if (useImmobilienApi) {
        return estateTypeId && marketingType
          ? getMarketingTypeEstateTypeIdLabel({
              estateSubTypes: catalogsEstateSubTypes,
              marketingType,
              estateTypeId,
            })
          : estateTypeId
          ? getMarketingTypeEstateTypeIdLabel({
              estateSubTypes: catalogsEstateSubTypes,
              estateTypeId,
            })
          : marketingType
          ? t(marketingType, { keyPrefix: 'savedSearches.marketingTypes' })
          : t('savedSearches.undefined');
      }
      return objectType && marketingType
        ? getMarketingTypeObjectTypeLabel({ t, marketingType, objectType })
        : objectType
        ? getMarketingTypeObjectTypeLabel({ t, objectType })
        : marketingType
        ? t(marketingType, { keyPrefix: 'savedSearches.marketingTypes' })
        : t('savedSearches.undefined');
    default:
      return '';
  }
};

export const getItemKey = (searchParams: EstateSearchProps): string => JSON.stringify(searchParams);
